import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Button from "@shared/v2/Button";
import Modal from "../../../shared/v2/Modal";
import Error from "../../../Reports/components/DashboardShared/Error";
import UsersDropdown from "./UsersDropdown";
import SourcesDropdown from "./SourcesDropdown";
import LeadAssignType from "./LeadAssignType";
import FirstToClaim from "./FirstToClaim";
import RoundRobin from "./RoundRobin";
import LeadCreation from "./LeadCreation";
import DeleteTrackingNumber from "./DeleteTrackingNumber";
import { updateTrackingNumber, updateVoicemailGreeting } from "./services";

import { useAccountCallRecordingSettings, useCurrentUser } from "../../../reducers/layoutReducer/selectors";
import VoicemailRecorder from "./VoicemailRecorder";
import TextMessagingSettings from "./TextMessagingSettings/TextMessagingSettings";
import Divider from "../../../Settings/Phone/Divider";
import CallRecording from "./CallRecording";
import FallBackOptions from "./FallBackOptions";
import TextInput from "@shared/v2/TextInput";

const TrackingNumberDetailsModal = ({
  routingMethod,
  setRoutingMethod,
  fallback,
  setFallback,
  selectedUsers,
  setSelectedUsers,
  selectedFallbackUser,
  setSelectedFallbackUser,
  voicemailGreetingChanged,
  setVoicemailGreetingChanged,
  voicemailGreetingFile,
  setVoicemailGreetingFile,
  voicemailGreetingFilename,
  setVoicemailGreetingFilename,
  selectedTextInboundUser,
  setSelectedTextInboundUsers,
  name,
  setName,
  sources,
  source,
  setSource,
  chosenNumber,
  createLead,
  setCreateLead,
  agents,
  admins,
  isNewNumber,
  onClose,
  showDetailsModal,
  inboundRecording,
  setInboundRecording,
  setInboundNotificationMessageCondition,
  inboundNotificationMessageCondition,
  trackingNumber,
  beginDeletionConfirmation,
  onNumbersChanged,
  purchaseNumber,
  backToNumberSelection,
  isEnabledInboundTexts,
  handleToggle,
}) => {
  const currentUser = useCurrentUser();
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState("");

  const FIRST_TO_CLAIM_LIMIT = 10;

  const accountCallRecordingSettings = useAccountCallRecordingSettings();
  const showTwoPartyNotice = inboundNotificationMessageCondition !== "always";
  const initialAgreeTwoParty = accountCallRecordingSettings.inboundNotificationMessageCondition !== "always";
  const [agreeTwoParty, setAgreeTwoParty] = useState(initialAgreeTwoParty);

  useEffect(() => {
    if (showTwoPartyNotice) setAgreeTwoParty(initialAgreeTwoParty);
  }, [showTwoPartyNotice]);

  useEffect(() => {
    if (accountCallRecordingSettings && showDetailsModal) {
      setAgreeTwoParty(initialAgreeTwoParty);
    }
  }, [accountCallRecordingSettings, showDetailsModal]);

  useEffect(() => {
    if (inboundRecording === "disabled") {
      setInboundNotificationMessageCondition("always");
    }
  }, [inboundRecording]);

  const update = async () => {
    setIsFetching(true);

    const { error: updateError } = await updateTrackingNumber(
      trackingNumber.id,
      name,
      source,
      routingMethod,
      selectedUsers,
      isEnabledInboundTexts,
      selectedTextInboundUser,
      createLead,
      fallback,
      selectedFallbackUser,
      voicemailGreetingFilename,
      inboundRecording,
      inboundNotificationMessageCondition,
    );

    if (voicemailGreetingChanged) {
      await updateVoicemailGreeting(trackingNumber.id, voicemailGreetingFile, voicemailGreetingFilename);
    }

    if (updateError) {
      setError(updateError);
      setIsFetching(false);
    } else {
      onNumbersChanged("settings");
    }
  };

  const canSubmit = isNewNumber
    ? !!name && !!chosenNumber && selectedUsers.length > 0
    : !!name && selectedUsers.length > 0;

  const saveText = () => {
    if (!isNewNumber) {
      return "Save";
    }

    return currentUser.account.isPlace ? "Activate Number" : "Buy Number";
  };

  const validAgents = agents.filter((agent) => agent.routingNumber !== null);

  return (
    <Modal
      show
      onHide={onClose}
      contentSize="medium"
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentClassName="tw-max-w-[615px] tw-max-h-[90vh] tw-w-full tw-flex tw-flex-col tw-gap-[32px] tw-px-[17px]"
    >
      <Modal.Header
        className="tw-px-[15px]"
        title={isNewNumber ? "Tracking Number Details" : "Edit Tracking Number"}
        closeButton
      >
        <Error message={error} show={Boolean(error)} onClose={() => setError("")} />
        <Divider />
      </Modal.Header>
      <Modal.Body className="tw-px-[15px] tw-flex tw-flex-col tw-overflow-auto ">
        <div className="tw-mb-24px tw-text-gray-75">
          {isNewNumber ? (
            <span>
              You've selected the tracking number <strong>{chosenNumber.label}</strong>. Now we'll just need
              some information about your new tracking number:
            </span>
          ) : (
            <span>
              Tracking numbers can be used for specific campaigns and sources, so you know where your calls
              and leads are coming from.
            </span>
          )}
        </div>
        <TextInput
          className="tw-mb-[24px]"
          label="Tracking Number Name"
          placeholder="Where will you use this tracking number?"
          isRequired
          onChange={(e) => setName(e.target?.value || "")}
          value={name}
        />
        <div className="tw-flex tw-flex-col">
          <div className="tw-mb-[8px]">
            <span className="tw-text-14d tw-text-gray-50 tw-font-semibold">
              Source for Tracking Number (optional)
            </span>
          </div>
          <div className="tw-mb-[4px]">
            <SourcesDropdown
              trackingNumberName={name}
              sources={sources}
              source={source}
              setSource={(value) => setSource(value)}
            />
          </div>
          <span className="tw-text-12d tw-text-gray-50 tw-font-normal">
            Automatically assigns this source to leads created from this number{" "}
          </span>
        </div>
        <Divider verticalMargin="tw-my-[24px]" />
        <div className="tw-flex tw-flex-col">
          <span className="tw-text-18d tw-text-gray-75 tw-font-normal tw-mb-[16px]">Call Routing</span>
          <div className="tw-my-[8px]">
            <span className="tw-text-14d tw-text-gray-50 tw-font-semibold after:tw-content-['*'] after:tw-text-brivity-coral-100 after:tw-ml-[4px]">
              Select who this number will be routed to
            </span>
          </div>
          <div className="tw-mb-[24px]">
            <UsersDropdown
              selectedUsers={selectedUsers}
              setSelectedUsers={setSelectedUsers}
              users={validAgents}
              placeholder={
                selectedUsers.length >= FIRST_TO_CLAIM_LIMIT && routingMethod === "first_to_claim"
                  ? "Maximum number of users reached"
                  : "Search or select users"
              }
            />
          </div>
          <div className="tw-mb-[24px]">
            <LeadAssignType
              assignType={routingMethod}
              setAssignType={setRoutingMethod}
              setFallback={setFallback}
            />
          </div>
          <div>
            {routingMethod === "first_to_claim" ? (
              <FirstToClaim agents={selectedUsers} setAgents={setSelectedUsers} />
            ) : (
              <RoundRobin agents={selectedUsers} setAgents={setSelectedUsers} />
            )}
          </div>
          <div className="tw-mb-[24px]">
            <FallBackOptions
              selectedFallbackUser={selectedFallbackUser}
              setSelectedFallbackUser={setSelectedFallbackUser}
              callIndividualOptions={admins}
              assignType={routingMethod}
              fallback={fallback}
              setFallback={setFallback}
            />
          </div>
        </div>

        <Divider verticalMargin="tw-my-[24px]" />
        <LeadCreation createLead={createLead} setCreateLead={setCreateLead} />
        <Divider verticalMargin="tw-my-[24px]" />
        <CallRecording
          inboundRecording={inboundRecording}
          setInboundRecording={setInboundRecording}
          setInboundNotificationMessageCondition={setInboundNotificationMessageCondition}
          inboundNotificationMessageCondition={inboundNotificationMessageCondition}
          showTwoPartyNotice={showTwoPartyNotice}
          agreeTwoParty={agreeTwoParty}
          setAgreeTwoParty={setAgreeTwoParty}
        />
        <Divider verticalMargin="tw-my-[24px]" />
        <VoicemailRecorder
          setVoicemailGreetingChanged={setVoicemailGreetingChanged}
          voicemailGreetingFile={voicemailGreetingFile}
          setVoicemailGreetingFile={setVoicemailGreetingFile}
          voicemailGreetingFilename={voicemailGreetingFilename}
          setVoicemailGreetingFilename={setVoicemailGreetingFilename}
        />

        <Divider verticalMargin="tw-my-[24px]" />
        <TextMessagingSettings
          isEnabledInboundTexts={isEnabledInboundTexts}
          handleToggle={handleToggle}
          selectedTextInboundUser={selectedTextInboundUser}
          textInboundUsers={admins}
          setSelectedTextInboundUsers={setSelectedTextInboundUsers}
        />

        <div className="tw-mb-[24px]" />
        {!isNewNumber && (
          <>
            <Divider verticalMargin="tw-mb-[24px]" />
            <div className="tw-mb-[24px]">
              <DeleteTrackingNumber
                beginDeletionConfirmation={beginDeletionConfirmation}
                isFetching={isFetching}
              />
            </div>
          </>
        )}
      </Modal.Body>
      <Modal.Footer className="tw-px-[15px] tw-flex tw-flex-col tw-gap-[16px]">
        <Divider />
        <div className="tw-flex tw-gap-[8px] tw-justify-between">
          <Button size="medium" schema="tertiary" onClick={isNewNumber ? backToNumberSelection : onClose}>
            {isNewNumber ? "Back" : "Cancel"}
          </Button>
          <Button
            size="medium"
            disabled={(showTwoPartyNotice && !agreeTwoParty) || !canSubmit || isFetching}
            onClick={isNewNumber ? purchaseNumber : update}
          >
            {saveText()}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

TrackingNumberDetailsModal.propTypes = {
  routingMethod: PropTypes.string.isRequired,
  setRoutingMethod: PropTypes.func.isRequired,
  selectedFallbackUser: PropTypes.shape({}).isRequired,
  setSelectedFallbackUser: PropTypes.func.isRequired,
  fallback: PropTypes.string.isRequired,
  setFallback: PropTypes.func.isRequired,
  selectedUsers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isEnabledInboundTexts: PropTypes.bool.isRequired,
  handleToggle: PropTypes.func.isRequired,
  selectedTextInboundUser: PropTypes.shape({}).isRequired,
  setSelectedUsers: PropTypes.func.isRequired,
  setSelectedUsers: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  setName: PropTypes.func.isRequired,
  source: PropTypes.number.isRequired,
  setSource: PropTypes.func.isRequired,
  chosenNumber: PropTypes.string.isRequired,
  createLead: PropTypes.bool.isRequired,
  setCreateLead: PropTypes.func.isRequired,
  beginDeletionConfirmation: PropTypes.func.isRequired,
  purchaseNumber: PropTypes.func.isRequired,
  backToNumberSelection: PropTypes.func.isRequired,
  agents: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  admins: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  onClose: PropTypes.func.isRequired,
  showDetailsModal: PropTypes.bool.isRequired,
  inboundRecording: PropTypes.string.isRequired,
  setInboundRecording: PropTypes.func.isRequired,
  setInboundNotificationMessageCondition: PropTypes.func.isRequired,
  inboundNotificationMessageCondition: PropTypes.string.isRequired,
  trackingNumber: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    createLead: PropTypes.bool.isRequired,
    source: PropTypes.string.isRequired,
    users: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    routingMethod: PropTypes.string.isRequired,
  }).isRequired,
  onNumbersChanged: PropTypes.func.isRequired,
  sources: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  isNewNumber: PropTypes.bool.isRequired,
};

export default TrackingNumberDetailsModal;
